import { signOutAndPurge, reportSignOutFailure } from '../common/pwaSignOut';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useClerk } from '@clerk/clerk-react';
import { useAuthContext } from '@/hooks/use_auth_context';
import { useIntl } from '@/react-intl';
import { featureRegistry } from '@/uptk/core/features/feature_registry';
import { loadModuleRegistryFromAuthContext } from '@/uptk/core/modules/registry';
import messages_en from '@/translations/en.json';
import { AlertTriangle } from 'lucide-react';

type AuthContextInitializerProps = {
  children: React.ReactNode;
  onContextReady?: () => void;
};

type StatusScreenProps = {
  message: string;
  title?: string;
  action?: React.ReactNode;
  tone?: 'loading' | 'error';
};

const isOnboardingRoute = (pathname: string) => {
  return pathname === '/onboarding/tenant';
};

const StatusScreen = ({ message, title, action, tone = 'loading' }: StatusScreenProps) => {
  const isError = tone === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? undefined : 'polite'}
      className="flex min-h-screen items-center justify-center bg-portal-bg px-6 text-portal-ink transition-colors"
    >
      <div className="w-full max-w-md rounded-2xl border border-portal-border bg-portal-surface p-6 shadow-sm">
        <div className="flex items-start gap-4">
          {isError ? (
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-10 w-10 shrink-0 text-theme-destructive" />
          ) : (
            <div className="mt-0.5 h-10 w-10 shrink-0 animate-spin motion-reduce:animate-none rounded-full border-2 border-portal-border border-t-portal-accent" />
          )}
          <div className="min-w-0 flex-1">
            {title ? <h2 className="text-lg font-semibold text-portal-ink">{title}</h2> : null}
            <p className={`${title ? 'mt-2' : ''} text-sm font-medium text-portal-ink-muted`}>{message}</p>
            {action ? <div className="mt-5">{action}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const AuthContextInitializer = ({ children, onContextReady }: AuthContextInitializerProps) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const { context, loading, error, warmingUp, refresh, isLoaded, isSignedIn } = useAuthContext();
  const onboardingRoute = isOnboardingRoute(location.pathname);

  useEffect(() => {
    if (context) {
      loadModuleRegistryFromAuthContext(context);
      featureRegistry.loadFromAuthContext(context);

      if (onContextReady) {
        onContextReady();
      }
    }
  }, [context, onContextReady]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !error) {
      return;
    }

    if (error.status === 404 && !onboardingRoute) {
      void navigate('/onboarding/tenant', { replace: true });
    }
  }, [error, isLoaded, isSignedIn, navigate, onboardingRoute]);

  // SSR safety: on the server and during the initial hydration pass,
  // isLoaded is false and we return null. On the client after mount,
  // Clerk resolves auth state and we render the real content.
  // To avoid hydration mismatches, always render the same loading
  // shell during SSR and the first client paint, then swap to the
  // real content once the client has mounted.
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => { setHasMounted(true); }, []);

  if (!hasMounted || !isLoaded) {
    return null;
  }

  if (!isSignedIn) {
    return <>{children}</>;
  }

  if (loading) {
    if (warmingUp) {
      return (
        <StatusScreen
          title={t('core.auth.warmingUpTitle')}
          message={t('core.auth.warmingUpMessage')}
        />
      );
    }
    return (
      <StatusScreen
        title={t('core.auth.loadingTitle')}
        message={t('core.auth.loadingMessage')}
      />
    );
  }

  if (error) {
    // Allow onboarding route to render even with 404
    if (error.status === 404 && onboardingRoute) {
      return <>{children}</>;
    }

    // For 404 errors (user not found), show loading while redirect happens
    if (error.status === 404) {
      return (
        <StatusScreen
          title={t('core.auth.redirectingTitle')}
          message={t('core.auth.redirectingMessage')}
        />
      );
    }

    // 403: user is not assigned to this tenant — permanent for this session.
    // Show a clear "not authorized" message with a sign-out button so the user
    // can switch accounts. Do NOT show a retry button — retrying would loop.
    if (error.status === 403) {
      return (
        <StatusScreen
          tone="error"
          title={t('core.auth.notAuthorizedTitle')}
          message={t('core.auth.notAuthorizedMessage')}
          action={(
            <button
              type="button"
              className="rounded-md bg-portal-accent px-4 py-2 text-sm font-medium text-portal-ink-on-accent transition-colors hover:opacity-90"
              onClick={() => void signOutAndPurge(signOut, { redirectUrl: "/" }).catch(reportSignOutFailure)}
            >
              {t('core.action.signOut')}
            </button>
          )}
        />
      );
    }

    // For other errors, show error screen with retry
    return (
      <StatusScreen
        tone="error"
        title={t('core.auth.failedTitle')}
        // i18n Rule 4a (UPT-183): show keyed, plain-language copy instead of the
        // raw error.message; the underlying error is logged by the auth layer.
        message={error.code === 'transaction.rollback_only' ? t('error.transaction.rollbackOnly') : (messages_en as Record<string, string>)['error.auth.contextLoadFailed']}
        action={(
          <button
            type="button"
            className="rounded-md bg-portal-accent px-4 py-2 text-sm font-medium text-portal-ink-on-accent transition-colors hover:opacity-90"
            onClick={() => {
              void refresh({ forceNetwork: true });
            }}
          >
            {t('core.action.retry')}
          </button>
        )}
      />
    );
  }

  return <>{children}</>;
};

export default AuthContextInitializer;

